// Generic Modal Manager
export class ModalManager {
  constructor() {
    this.modals = new Map();
    
    // Listen for other menus opening to close context menu
    document.addEventListener('menuOpening', (e) => {
      if (e.detail.menuId !== 'context-menu') {
        const existingMenu = document.getElementById('context-menu');
        if (existingMenu) {
          existingMenu.remove();
        }
      }
    });
  }

  /**
   * Register a modal element
   * @param {string} id - Modal ID
   * @param {HTMLElement} element - Modal element
   */
  register(id, element) {
    this.modals.set(id, element);
  }

  /**
   * Show a modal
   * @param {string} id - Modal ID
   */
  show(id) {
    const modal = this.modals.get(id);
    if (modal) {
      modal.classList.remove('hidden');
    }
  }

  /**
   * Hide a modal
   * @param {string} id - Modal ID
   */
  hide(id) {
    const modal = this.modals.get(id);
    if (modal) {
      modal.classList.add('hidden');
    }
  }

  /**
   * Hide all modals
   */
  hideAll() {
    this.modals.forEach(modal => modal.classList.add('hidden'));
  }

  /**
   * Create and show a confirmation dialog
   * @param {string} message - Confirmation message
   * @returns {Promise<boolean>} - True if confirmed
   */
  async confirm(message) {
    return window.confirm(message);
  }

  /**
   * Create and show a prompt dialog
   * @param {string} message - Prompt message
   * @param {string} defaultValue - Default input value
   * @returns {Promise<string|null>} - Input value or null if cancelled
   */
  async prompt(message, defaultValue = '') {
    return window.prompt(message, defaultValue);
  }

  /**
   * Create and show a context menu
   * @param {Array} items - Menu items { label, action }
   * @param {number} x - X position
   * @param {number} y - Y position
   * @param {HTMLElement} triggerButton - Optional button element to style when menu is open
   * @returns {HTMLElement} - Menu element
   */
  showContextMenu(items, x, y, triggerButton = null) {
    // Dispatch event to close other menus
    document.dispatchEvent(new CustomEvent('menuOpening', {
      detail: { menuId: 'context-menu' }
    }));
    
    // Remove existing context menu
    const existingMenu = document.getElementById('context-menu');
    if (existingMenu) existingMenu.remove();

    const menu = document.createElement('div');
    menu.id = 'context-menu';
    menu.className = 'fixed bg-gray-900 border border-gray-600 shadow-2xl py-1 z-50 min-w-[240px]';
    // Add backdrop blur for better visibility
    menu.style.backdropFilter = 'blur(8px)';
    menu.style.backgroundColor = 'rgba(17, 24, 39, 0.95)'; // gray-900 with slight transparency
    
    // Border radius - rounded left corners, square top-right if connected to button
    if (triggerButton) {
      menu.style.borderTopLeftRadius = '0.5rem';
      menu.style.borderTopRightRadius = '0';
      menu.style.borderBottomLeftRadius = '0.5rem';
      menu.style.borderBottomRightRadius = '0.5rem';
      menu.style.paddingTop = '0.25rem';
      menu.style.paddingBottom = '0.25rem';
    } else {
      menu.style.borderRadius = '0.5rem';
    }
    
    // Style trigger button if provided
    if (triggerButton) {
      triggerButton.style.backgroundColor = 'rgba(17, 24, 39, 0.95)';
      triggerButton.style.backdropFilter = 'blur(8px)';
      triggerButton.style.border = '1px solid rgb(75, 85, 99)';
      triggerButton.style.borderBottom = 'none';
      triggerButton.style.borderTopLeftRadius = '0.5rem';
      triggerButton.style.borderTopRightRadius = '0.5rem';
      triggerButton.style.borderBottomLeftRadius = '0';
      triggerButton.style.borderBottomRightRadius = '0';
      triggerButton.style.position = 'relative';
      triggerButton.style.zIndex = '51'; // Above menu
    }
    
    items.forEach(item => {
      if (item.separator) {
        const separator = document.createElement('div');
        separator.className = 'border-t border-gray-700 my-1';
        menu.appendChild(separator);
      } else if (item.customHTML) {
        // Custom HTML item (for controls like sliders, inputs)
        const customDiv = document.createElement('div');
        customDiv.className = 'px-4 py-2 text-sm text-gray-300';
        customDiv.innerHTML = item.customHTML;
        
        // Run initialization function if provided
        if (item.onMount) {
          setTimeout(() => item.onMount(customDiv), 0);
        }
        
        menu.appendChild(customDiv);
      } else {
        const button = document.createElement('button');
        button.className = `w-full text-left px-4 py-2 text-sm ${item.danger ? 'text-red-400' : 'text-gray-300'} ${item.disabled ? 'opacity-50 cursor-default' : 'hover:bg-gray-700'} flex items-center space-x-2`;
        button.innerHTML = `
          ${item.icon || ''}
          <span>${item.label}</span>
        `;
        if (!item.disabled) {
          button.addEventListener('click', () => {
            item.action();
            menu.remove();
            
            // Reset trigger button styling when menu is removed via item click
            if (triggerButton) {
              triggerButton.style.backgroundColor = '';
              triggerButton.style.backdropFilter = '';
              triggerButton.style.border = '';
              triggerButton.style.borderBottom = '';
              triggerButton.style.borderTopLeftRadius = '';
              triggerButton.style.borderTopRightRadius = '';
              triggerButton.style.borderBottomLeftRadius = '';
              triggerButton.style.borderBottomRightRadius = '';
              triggerButton.style.position = '';
              triggerButton.style.zIndex = '';
            }
          });
        }
        menu.appendChild(button);
      }
    });

    document.body.appendChild(menu);

    // Position menu - directly below trigger button if provided
    if (triggerButton) {
      const triggerRect = triggerButton.getBoundingClientRect();
      menu.style.right = `${window.innerWidth - triggerRect.right}px`;
      menu.style.left = 'auto';
      menu.style.top = `${triggerRect.bottom - 1}px`;
    } else {
      menu.style.left = `${x}px`;
      menu.style.top = `${y}px`;
    }

    // Adjust position if menu goes off screen (only for non-button menus)
    if (!triggerButton) {
      setTimeout(() => {
        const rect = menu.getBoundingClientRect();
        if (rect.right > window.innerWidth) {
          menu.style.left = `${x - rect.width}px`;
        }
        if (rect.bottom > window.innerHeight) {
          menu.style.top = `${y - rect.height}px`;
        }
      }, 0);
    }

    // Close menu when clicking outside
    const closeMenu = (e) => {
      if (!menu.contains(e.target)) {
        menu.remove();
        document.removeEventListener('click', closeMenu);
        
        // Reset trigger button styling
        if (triggerButton) {
          triggerButton.style.backgroundColor = '';
          triggerButton.style.backdropFilter = '';
          triggerButton.style.border = '';
          triggerButton.style.borderBottom = '';
          triggerButton.style.borderTopLeftRadius = '';
          triggerButton.style.borderTopRightRadius = '';
          triggerButton.style.borderBottomLeftRadius = '';
          triggerButton.style.borderBottomRightRadius = '';
          triggerButton.style.position = '';
          triggerButton.style.zIndex = '';
        }
      }
    };
    
    setTimeout(() => {
      document.addEventListener('click', closeMenu);
    }, 0);

    return menu;
  }
}

